import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch, useSelector, useStore } from 'react-redux';
import { change as rf_change } from 'redux-form';
import { InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { clearDtpAdjustmentMessage } from '../../redux/modules/dateTimePickerAdjustments';
import {
    toMoment,
    displayString,
    fillMissingTime,
    parseInput,
    buildOutMomentFromDraft,
    shouldSnapEndOfDayOnUserPick,
    dtpLog,
    isUserPickedChange,
    isTypingInputEvent,
    isWithinRecentTypingProbes,
    isCompleteNativeValue,
} from './dateTimePickerCore';
import { applyPeerSync } from './applyPeerSync';
import { mapPeerBounds } from './mapPeerBounds';
import './DateTimePicker.css';

function DateTimePicker(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const store = useStore();
    const {
        input,
        name,
        required,
        disabled,
        timeFormat,
        isValidDate,
        min,
        max,
        endOfDayIfMidnight,
        className,
        onChange: customOnChange,
        prependLabel,
        compact,
        openAt,
        openAtYearsAgo,
        minBoundField,
        maxBoundField,
        syncBoundPeer,
        peerMaxRange,
        invalidDateMessageKey,
        /** When this changes (e.g. period type in parent form), clear inline date validation message */
        validDateContextKey,
        /** Do not render the error under the input (parent places it via onInvalidMessageChange). */
        suppressInvalidMessageDisplay,
        onInvalidMessageChange,
    } = props;

    const meta = props.meta;
    const doSyncPeer = syncBoundPeer !== false
        && (minBoundField || maxBoundField);

    const withTime = timeFormat !== false;
    const prior = toMoment(input?.value);
    const [previewDisplay, setPreviewDisplay] = useState('');
    const [draftDisplay, setDraftDisplay] = useState(null);
    const [invalidMessage, setInvalidMessage] = useState('');
    const lastKeyboardAtRef = useRef(0);
    const lastBeforeInputAtRef = useRef(0);
    const lastInputAtRef = useRef(0);
    const lastInputWasTypingRef = useRef(false);
    const lastCommittedIsoRef = useRef(null);
    const pendingCommitIsoRef = useRef(null);
    const isValidDateRef = useRef(isValidDate);
    isValidDateRef.current = isValidDate;

    const previewDefaultMoment = useMemo(() => {
        if (openAt) {
            const m = toMoment(openAt);
            return m && m.isValid() ? m : null;
        }
        if (typeof openAtYearsAgo === 'number' && !Number.isNaN(openAtYearsAgo)) {
            return moment().subtract(openAtYearsAgo, 'years').startOf('year');
        }
        return null;
    }, [openAt, openAtYearsAgo]);

    const adjustmentMsg = useSelector((state) => {
        const f = meta?.form;
        const n = input?.name;
        if (!f || !n) return null;
        return state.dateTimePickerAdjustments?.[`${f}::${n}`] || null;
    });

    useEffect(() => {
        const m = toMoment(input?.value);
        if (!m || !m.isValid()) {
            setInvalidMessage('');
            return;
        }
        const draft = displayString(m, withTime);
        const built = buildOutMomentFromDraft(draft, {
            withTime,
            endOfDayIfMidnight,
            onBlur: true,
            isValidDate: (d) => (typeof isValidDateRef.current === 'function'
                ? isValidDateRef.current(d)
                : true),
        });
        if (built.kind === 'invalidRule' || built.kind === 'invalidParse') {
            setInvalidMessage(t(invalidDateMessageKey || 'programmations.invalid_period_date'));
        } else {
            setInvalidMessage('');
        }
    }, [validDateContextKey, invalidDateMessageKey, input?.value, withTime, endOfDayIfMidnight, t]);

    useEffect(() => {
        if (typeof onInvalidMessageChange !== 'function')
            return undefined;
        onInvalidMessageChange(invalidMessage || '');
        return undefined;
    }, [invalidMessage, onInvalidMessageChange]);

    useEffect(() => {
        const m = toMoment(input?.value);
        if (!m?.isValid())
            return;
        const iso = m.toISOString();
        const pending = pendingCommitIsoRef.current;
        if (pending) {
            const pendingM = toMoment(pending);
            if (pendingM?.isValid()) {
                if (m.isSame(pendingM, withTime ? 'minute' : 'day')) {
                    lastCommittedIsoRef.current = iso;
                    pendingCommitIsoRef.current = null;
                }
                return;
            }
        }
        lastCommittedIsoRef.current = iso;
    }, [input?.value, withTime]);

    useEffect(() => {
        if (draftDisplay == null)
            return;
        const committed = toMoment(input?.value);
        if (committed?.isValid() && draftDisplay === displayString(committed, withTime))
            setDraftDisplay(null);
    }, [draftDisplay, input?.value, withTime]);

    if (!input) {
        return null;
    }

    const resolveCommitted = () => {
        const fromInput = toMoment(input?.value);
        const fromRef = toMoment(lastCommittedIsoRef.current);
        if (fromRef?.isValid() && fromInput?.isValid()) {
            // Our ref is set synchronously on commit; redux-form props can lag one
            // frame (spinner 23→22→23: props still 23 while ref is already 22).
            if (!fromRef.isSame(fromInput, withTime ? 'minute' : 'day'))
                return fromRef;
            return fromInput;
        }
        if (fromInput?.isValid())
            return fromInput;
        return fromRef;
    };

    const processAndCommit = (draft, options = {}) => {
        const onBlur = options.onBlur === true;
        const snapEndOfDay = options.snapEndOfDay === true;
        const dtpLogBlurBuilt = options.dtpLogBlurBuild === true;

        if (draft === '' || draft == null) {
            input.onChange(null);
            if (customOnChange)
                customOnChange(null);
            return true;
        }

        const built = buildOutMomentFromDraft(draft, {
            withTime,
            endOfDayIfMidnight,
            onBlur,
            fromCalendar: snapEndOfDay,
            isValidDate,
        });
        if (dtpLogBlurBuilt) {
            dtpLog('blur:built', {
                name: input?.name,
                form: meta?.form,
                draft,
                kind: built.kind,
                out: built.kind === 'ok' ? built.outMoment.toISOString() : undefined,
                store: input?.value,
            });
        }
        if (built.kind === 'invalidParse' || built.kind === 'invalidRule') {
            if (onBlur)
                setInvalidMessage(t(invalidDateMessageKey || 'programmations.invalid_period_date'));
            return false;
        }
        if (built.kind !== 'ok') {
            return false;
        }
        const { outMoment } = built;

        const fromInput = toMoment(input?.value);
        const unit = withTime ? 'minute' : 'day';
        const needsCommit = !fromInput?.isValid()
            || !outMoment.isValid()
            || !fromInput.isSame(outMoment, unit);

        if (!needsCommit) {
            dtpLog('no-op (same as store)', {
                name: input?.name,
                form: meta?.form,
                draft,
                store: input?.value,
                out: outMoment.toISOString(),
            });
            if (onBlur && meta?.form && input?.name) {
                dispatch(clearDtpAdjustmentMessage(meta.form, input.name));
            }
            return true;
        }

        dtpLog('onChange (value differs)', {
            name: input?.name,
            form: meta?.form,
            draft,
            fromStore: input?.value,
            to: outMoment.toISOString(),
        });
        const iso = outMoment.toISOString();
        input.onChange(iso);
        if (meta?.form && input?.name) {
            dispatch(rf_change(meta.form, input.name, iso));
        }
        lastCommittedIsoRef.current = iso;
        pendingCommitIsoRef.current = iso;

        if (doSyncPeer && meta?.form) {
            applyPeerSync({
                dispatch,
                getState: store.getState,
                form: meta.form,
                withTime,
                minBoundField,
                maxBoundField,
                peerMaxRange,
                outMoment,
            });
        }

        if (customOnChange) {
            customOnChange(withTime ? outMoment : outMoment.clone());
        }

        return true;
    };

    const handleChange = (e) => {
        const str = e.target.value;
        const now = Date.now();
        const committedNow = resolveCommitted();
        const currentDisplay = (
            draftDisplay != null ? draftDisplay :
            (prior ? displayString(prior, withTime) : (previewDisplay || ''))
        );
        // Match the on-screen empty-field preview (start/end of day), not only `openAt`.
        // Otherwise a typed/picked date that keeps T00:00 can be treated as an implicit
        // echo and dropped before blur — the field then resets to empty.
        const openAtHint = previewDefaultMoment
            || (withTime && !committedNow?.isValid() && previewDisplay
                ? parseInput(previewDisplay, true)
                : null)
            || (withTime && !committedNow?.isValid() && !previewDefaultMoment
                ? (endOfDayIfMidnight ? moment().endOf('day') : moment().startOf('day'))
                : null);
        const typingRecent = isWithinRecentTypingProbes(
            now,
            lastKeyboardAtRef.current,
            lastBeforeInputAtRef.current,
        );
        if (withTime && (input?.value == null || input?.value === '') && !committedNow?.isValid()) {
            const userPickedEmpty = isUserPickedChange(
                e, str, withTime, committedNow, openAtHint, currentDisplay, typingRecent,
            );
            if (!userPickedEmpty) {
                if (!typingRecent && !isTypingInputEvent(e)) {
                    const previewStr = previewDisplay
                        || (openAtHint ? displayString(openAtHint, true) : '');
                    // Pure echo of the focus preview — ignore. A different complete
                    // value (e.g. another day at 00:00) must stay as draft for blur.
                    if (!isCompleteNativeValue(str, true) || str === previewStr) {
                        dtpLog('withTime change ignored (likely implicit)', {
                            name: input?.name,
                            form: meta?.form,
                            str,
                        });
                        return;
                    }
                }
            }
        }

        if (previewDisplay)
            setPreviewDisplay('');
        if (invalidMessage)
            setInvalidMessage('');
        if (adjustmentMsg && meta?.form && input?.name)
            dispatch(clearDtpAdjustmentMessage(meta.form, input.name));
        const userPicked = isUserPickedChange(
            e, str, withTime, committedNow, openAtHint, currentDisplay, typingRecent,
        );
        const keyboardEditing = isTypingInputEvent(e)
            || lastInputWasTypingRef.current
            || isWithinRecentTypingProbes(
                now,
                lastKeyboardAtRef.current,
                lastBeforeInputAtRef.current,
            );
        lastInputWasTypingRef.current = false;
        const deferCommit = keyboardEditing;

        if (withTime) {
            lastInputAtRef.current = now;
            if (userPicked) {
                const parsed = parseInput(str, true);
                const snapEod = shouldSnapEndOfDayOnUserPick(
                    parsed, committedNow, endOfDayIfMidnight, currentDisplay, str, withTime,
                );
                if (!deferCommit && processAndCommit(str, {
                    onBlur: true,
                    snapEndOfDay: snapEod,
                    userPicked: true,
                })) {
                    setDraftDisplay(null);
                } else
                    setDraftDisplay(str);
            } else
                setDraftDisplay(str);
            return;
        }
        // Date-only: defer keyboard typing to blur; calendar picks commit immediately.
        lastInputAtRef.current = now;
        if (userPicked && !deferCommit) {
            if (processAndCommit(str, { onBlur: true, snapEndOfDay: true, userPicked: true }))
                setDraftDisplay(null);
            else
                setDraftDisplay(str);
        } else
            setDraftDisplay(str);
        return;
    };

    const display = (
        draftDisplay != null ? draftDisplay :
        (prior ? displayString(prior, withTime) : (previewDisplay || ''))
    );

    const handleMouseDown = () => {
        if (!props.noPreviewOnFocus && !prior && !previewDisplay && !draftDisplay && previewDefaultMoment) {
            const s = displayString(previewDefaultMoment, withTime);
            if (s)
                setPreviewDisplay(s);
        }
    };

    const handleFocus = (e) => {
        dtpLog('focus', {
            name: input?.name,
            form: meta?.form,
            store: input?.value,
            draftDisplay,
            previewDisplay,
        });
        input.onFocus && input.onFocus(e);
        if (withTime && draftDisplay != null) {
            const filled = fillMissingTime(draftDisplay, Boolean(endOfDayIfMidnight));
            if (filled && filled !== draftDisplay)
                setDraftDisplay(filled);
        }
        if (!props.noPreviewOnFocus && !prior && !previewDisplay) {
            const base = previewDefaultMoment
                || (withTime
                    ? (endOfDayIfMidnight ? moment().endOf('day') : moment().startOf('day'))
                    : moment());
            const s = displayString(base, withTime);
            if (s)
                setPreviewDisplay(s);
        }
    };

    const handleBlur = (e) => {
        dtpLog('blur:start', {
            name: input?.name,
            form: meta?.form,
            store: input?.value,
            draftDisplay,
            previewDisplay,
        });
        if (!prior && previewDisplay)
            setPreviewDisplay('');
        // Some browsers flush datetime-local only on blur (especially when the user
        // typed the date and left the default 00:00 untouched). Prefer draft, else
        // the native value when it is complete and differs from the store — but never
        // promote an untouched focus preview into a committed value.
        let draft = draftDisplay;
        const nativeValue = e?.target?.value;
        if ((draft == null || draft === '')
            && nativeValue
            && nativeValue !== previewDisplay
            && isCompleteNativeValue(nativeValue, withTime)) {
            const nativeM = parseInput(nativeValue, withTime);
            const committed = resolveCommitted();
            if (nativeM?.isValid()
                && (!committed?.isValid()
                    || !committed.isSame(nativeM, withTime ? 'minute' : 'day'))) {
                draft = withTime
                    ? (fillMissingTime(nativeValue, Boolean(endOfDayIfMidnight)) || nativeValue)
                    : nativeValue;
            }
        }
        setDraftDisplay(null);
        if (draft != null && draft !== '') {
            const committed = resolveCommitted();
            const draftM = parseInput(draft, withTime);
            if (committed?.isValid() && draftM?.isValid()
                && committed.isSame(draftM, withTime ? 'minute' : 'day')) {
                dtpLog('blur:end (draft matches store)', {
                    name: input?.name,
                    form: meta?.form,
                });
            } else {
                processAndCommit(draft, { onBlur: true, dtpLogBlurBuild: true });
            }
        } else if (draft === '') {
            processAndCommit('', { onBlur: true, dtpLogBlurBuild: true });
        } else {
            if (meta?.form && input?.name) {
                dispatch(clearDtpAdjustmentMessage(meta.form, input.name));
                const committed = resolveCommitted();
                if (committed?.isValid()) {
                    const iso = committed.toISOString();
                    const storeM = toMoment(input.value);
                    if (storeM?.isValid()
                        && storeM.isSame(committed, withTime ? 'minute' : 'day')
                        && input.value !== iso) {
                        dispatch(rf_change(meta.form, input.name, iso));
                        lastCommittedIsoRef.current = iso;
                    }
                }
            }
            dtpLog('blur:end (no draft)', { name: input?.name, form: meta?.form });
        }
        input.onBlur && input.onBlur(e);
    };

    const handleKeyDown = () => {
        lastKeyboardAtRef.current = Date.now();
    };

    const handleBeforeInput = () => {
        lastBeforeInputAtRef.current = Date.now();
    };

    const handleInput = (e) => {
        lastInputAtRef.current = Date.now();
        lastInputWasTypingRef.current = isTypingInputEvent(e);
    };

    const dismissAdjustment = (ev) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        if (meta?.form && input?.name) {
            dispatch(clearDtpAdjustmentMessage(meta.form, input.name));
        }
    };

    const inputEl = (
        <input
            type={withTime ? 'datetime-local' : 'date'}
            name={name || input.name}
            id={props.id}
            value={display}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onMouseDown={handleMouseDown}
            onBeforeInput={handleBeforeInput}
            onInput={handleInput}
            onBlur={handleBlur}
            onFocus={handleFocus}
            required={required}
            disabled={disabled}
            min={min}
            max={max}
            aria-invalid={invalidMessage ? 'true' : undefined}
            className={`form-control date-picker ${className || ''}`.trim()}
            autoComplete={props.autoComplete}
        />
    );

    const rootClass = `DateTimePicker${compact ? ' compact' : ''}${prependLabel ? ' with-prepend' : ''}`;

    const errorEl = (!suppressInvalidMessageDisplay && invalidMessage) ? (
        <div
            className="alert alert-danger alert-inline mt-2 mb-0 py-2 px-3 small"
            role="alert"
        >
            <span className="alert-inline-body">{invalidMessage}</span>
        </div>
    ) : null;

    const adjustmentEl = adjustmentMsg ? (
        <div
            className="alert alert-info alert-inline mt-2 mb-0 py-2 px-3 small DateTimePicker__adjustment-note"
            role="button"
            tabIndex={0}
            onClick={dismissAdjustment}
            onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    dismissAdjustment(ev);
                }
            }}
        >
            <span className="alert-inline-body">
                {t(`datetime_picker.adjusted_${adjustmentMsg.kind}`, {
                    range: adjustmentMsg.range,
                })}
            </span>
        </div>
    ) : null;

    if (prependLabel) {
        return (
            <div className={rootClass} data-form={props.meta?.form}>
                <InputGroup>
                    <InputGroupAddon addonType="prepend">
                        <InputGroupText className="text-nowrap">{prependLabel}</InputGroupText>
                    </InputGroupAddon>
                    {inputEl}
                </InputGroup>
                {errorEl}
                {adjustmentEl}
            </div>
        );
    }

    return (
        <div className={rootClass} data-form={props.meta?.form}>
            {inputEl}
            {errorEl}
            {adjustmentEl}
        </div>
    );
}

export default connect(mapPeerBounds)(DateTimePicker);
